import { useLocation, Link } from 'react-router-dom';
import { RefreshCw, Bell } from 'lucide-react';
import { useHealthStore } from '@/stores/health';
import { useAppStore } from '@/stores/app';
import { clsx } from 'clsx';
import { matchNavItem } from './nav';
import { ClientSelector } from '@/components/ClientSelector';
import { WorkbenchPersonaChip } from '@/components/WorkbenchPersonaChip';

function formatSlug(slug: string): string {
  return slug.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

const Sep = () => <span className="text-[8px] text-slate-300">/</span>;

export function TopBar() {
  const { pathname } = useLocation();
  const { health, loading, fetchAll, initialized } = useHealthStore();
  const notifications = useAppStore((s) => s.notifications);

  const isOnline = health?.status === 'healthy';
  const isConnecting = !initialized;

  // Dynamic persona detail route: /personas/:clientUid/:personaSlug
  const personaDetailMatch = pathname.match(/^\/personas\/([^/]+)\/([^/]+)$/);

  // Dynamic chat route: /chat/:threadId
  const chatThreadMatch = !personaDetailMatch && pathname.match(/^\/chat\/(.+)$/);

  // Best-match nav item drives section + label for every other route
  const navItem = matchNavItem(pathname);

  return (
    <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-6 z-20 shadow-sm flex-shrink-0">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
        <Link to="/" className="hover:text-navy-900 transition-colors">
          Home
        </Link>

        {personaDetailMatch ? (
          <>
            <Sep />
            <span>Cognitive Layer</span>
            <Sep />
            <Link to="/personas" className="hover:text-navy-900 transition-colors">
              Personas
            </Link>
            <Sep />
            <Link
              to={`/personas/${personaDetailMatch[1]}`}
              className="hover:text-navy-900 transition-colors"
            >
              {formatSlug(personaDetailMatch[1])}
            </Link>
            <Sep />
            <span className="text-navy-900 font-semibold bg-slate-100 px-2 py-0.5 rounded">
              {formatSlug(personaDetailMatch[2])}
            </span>
          </>
        ) : chatThreadMatch ? (
          <>
            <Sep />
            <span>Creative Workbench</span>
            <Sep />
            <Link to="/chat/new" className="hover:text-navy-900 transition-colors">
              Chat
            </Link>
            <Sep />
            <span className="text-navy-900 font-semibold bg-slate-100 px-2 py-0.5 rounded">
              {formatSlug(chatThreadMatch[1])}
            </span>
          </>
        ) : navItem ? (
          <>
            {navItem.section && (
              <>
                <Sep />
                <span>{navItem.section}</span>
              </>
            )}
            <Sep />
            <span className="text-navy-900 font-semibold bg-slate-100 px-2 py-0.5 rounded">
              {navItem.label}
            </span>
          </>
        ) : null}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        {/* Workbench persona indicator — only visible on /creative routes when a persona is active */}
        <WorkbenchPersonaChip />

        {/* Client selector */}
        <ClientSelector />

        <div className="h-5 w-px bg-slate-200" />

        {/* Status indicator */}
        <div
          className={clsx(
            'flex items-center gap-2 text-[10px] uppercase font-bold px-3 py-1 rounded-full border shadow-sm',
            isConnecting
              ? 'bg-slate-50 text-slate-500 border-slate-200'
              : isOnline
              ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
              : 'bg-red-50 text-red-600 border-red-100',
          )}
        >
          <span
            className={clsx(
              'w-2 h-2 rounded-full',
              isConnecting
                ? 'bg-slate-300 animate-pulse'
                : isOnline
                ? 'bg-emerald-500 animate-pulse-ring'
                : 'bg-red-400',
            )}
          />
          <span>{isConnecting ? 'Connecting' : isOnline ? 'Online' : 'Offline'}</span>
        </div>

        <div className="h-5 w-px bg-slate-200" />

        {/* Notifications */}
        <button
          className="relative text-slate-400 hover:text-slate-600 transition-colors"
          title="Notifications"
        >
          <Bell size={18} />
          {notifications.length > 0 && (
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-brand-coral text-white text-[9px] font-bold rounded-full flex items-center justify-center">
              {notifications.length}
            </span>
          )}
        </button>

        {/* Refresh */}
        <button
          onClick={() => fetchAll()}
          disabled={loading}
          className="text-slate-400 hover:text-brand-teal transition-colors disabled:opacity-40"
          title="Refresh status"
        >
          <RefreshCw
            size={16}
            className={clsx(loading && 'animate-spin')}
          />
        </button>
      </div>
    </header>
  );
}
